import { useState } from 'react';
import { TerminalWindow } from './TerminalWindow';
import {
  SubsectionLabel,
  InfoPanel,
  Button,
  StepDotsNavigation,
} from './shared';
import type { AlternateExplainerStep } from '../content/demos/alternateScreenDemo';
import {
  alternateScreenDemoUi as altUi,
  alternateScreenExplainerSteps as EXPLAINER_STEPS,
  alternateScreenNormalContent as NORMAL_SCREEN_CONTENT,
  alternateScreenVimBody as ALTERNATE_SCREEN_CONTENT,
} from '../content/demos/alternateScreenDemo';

type ExplainerStep = AlternateExplainerStep;

export function AlternateScreenDemo() {
  const [isAlternateScreen, setIsAlternateScreen] = useState(false);
  const [currentStep, setCurrentStep] = useState<ExplainerStep>('what');

  const steps = Object.keys(EXPLAINER_STEPS) as ExplainerStep[];
  const stepContent = EXPLAINER_STEPS[currentStep];

  const toggleScreen = () => {
    setIsAlternateScreen((prev) => !prev);
  };

  return (
    <div className="space-y-8">
      {/* Main Demo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Terminal Simulation */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Button size="sm" onClick={toggleScreen}>
              {isAlternateScreen ? altUi.exitVim : altUi.openVim}
            </Button>
            <span
              className={`px-2 py-1 text-xs ${
                isAlternateScreen
                  ? 'bg-terminal-cyan/20 text-terminal-cyan'
                  : 'bg-terminal-green/20 text-terminal-green'
              }`}
            >
              {isAlternateScreen
                ? altUi.bufferAlternate
                : altUi.bufferNormal}
            </span>
          </div>

          <TerminalWindow>
            <div className="font-mono text-sm min-h-[300px] relative overflow-hidden">
              {/* Normal screen with transition */}
              <div
                className={`absolute inset-0 transition-all duration-300 ${
                  isAlternateScreen
                    ? 'opacity-0 transform -translate-x-full'
                    : 'opacity-100 transform translate-x-0'
                }`}
              >
                {NORMAL_SCREEN_CONTENT.map((line, idx) => (
                  <div
                    key={idx}
                    className={
                      line.startsWith('$')
                        ? 'text-terminal-green'
                        : line.startsWith('drwx') || line.startsWith('-rw')
                          ? 'text-terminal-cyan'
                          : 'text-terminal-fg'
                    }
                  >
                    {line}
                  </div>
                ))}
              </div>

              {/* Alternate screen (vim) with transition */}
              <div
                className={`absolute inset-0 transition-all duration-300 ${
                  isAlternateScreen
                    ? 'opacity-100 transform translate-x-0'
                    : 'opacity-0 transform translate-x-full'
                }`}
              >
                {/* Vim header */}
                <div className="bg-terminal-blue/20 text-terminal-blue px-2 mb-1 text-xs">
                  {altUi.vimBanner}
                </div>
                {/* Vim content */}
                {ALTERNATE_SCREEN_CONTENT.map((line, idx) => (
                  <div
                    key={idx}
                    className={
                      line === '~'
                        ? 'text-terminal-blue'
                        : line.startsWith('#')
                          ? 'text-terminal-yellow'
                          : line.startsWith('```')
                            ? 'text-terminal-green'
                            : 'text-terminal-fg'
                    }
                  >
                    {line || '\u00A0'}
                  </div>
                ))}
                {/* Vim status line */}
                <div className="absolute bottom-0 left-0 right-0 bg-terminal-dim text-terminal-bg px-2 text-xs flex justify-between">
                  <span>{altUi.vimStatusLeft}</span>
                  <span>{altUi.vimStatusRight}</span>
                </div>
              </div>
            </div>
          </TerminalWindow>
        </div>

        {/* Escape Sequences */}
        <div className="space-y-4">
          <SubsectionLabel>{altUi.subsectionEscapes}</SubsectionLabel>

          <div className="space-y-3">
            <div
              className={`p-4 border transition-all ${
                !isAlternateScreen
                  ? 'border-terminal-green bg-terminal-green/5'
                  : 'border-terminal-border'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-terminal-green font-bold text-sm">
                  {altUi.enterAltTitle}
                </span>
                <code className="text-terminal-yellow text-xs">
                  {altUi.enterAltEsc}
                </code>
              </div>
              <p className="text-terminal-muted text-sm">
                {altUi.enterAltDesc}
              </p>
            </div>

            <div
              className={`p-4 border transition-all ${
                isAlternateScreen
                  ? 'border-terminal-cyan bg-terminal-cyan/5'
                  : 'border-terminal-border'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-terminal-cyan font-bold text-sm">
                  {altUi.exitAltTitle}
                </span>
                <code className="text-terminal-yellow text-xs">
                  {altUi.exitAltEsc}
                </code>
              </div>
              <p className="text-terminal-muted text-sm">
                {altUi.exitAltDesc}
              </p>
            </div>
          </div>

          {/* Visual representation of buffer swap */}
          <div className="bg-terminal-highlight border border-terminal-border p-4 space-y-3">
            <div className="text-terminal-fg text-sm font-bold">
              {altUi.layoutTitle}
            </div>
            <div className="flex items-center gap-4">
              <div
                className={`flex-1 p-3 border-2 text-center text-sm transition-all ${
                  !isAlternateScreen
                    ? 'border-terminal-green bg-terminal-green/10 text-terminal-green'
                    : 'border-terminal-border text-terminal-dim'
                }`}
              >
                {altUi.bufferNormal}
                <div className="text-xs mt-1 opacity-70">
                  {altUi.bufferHistorySubtitle}
                </div>
              </div>
              <div className="text-terminal-dim">⇄</div>
              <div
                className={`flex-1 p-3 border-2 text-center text-sm transition-all ${
                  isAlternateScreen
                    ? 'border-terminal-cyan bg-terminal-cyan/10 text-terminal-cyan'
                    : 'border-terminal-border text-terminal-dim'
                }`}
              >
                {altUi.bufferAlternate}
                <div className="text-xs mt-1 opacity-70">
                  {altUi.bufferCanvasSubtitle}
                </div>
              </div>
            </div>
            <p className="text-terminal-dim text-xs text-center">
              {altUi.layoutCaption}
            </p>
          </div>
        </div>
      </div>

      {/* Explainer */}
      <InfoPanel className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="text-terminal-red font-medium text-sm">
            {stepContent.title}
          </div>
          <StepDotsNavigation
            steps={steps}
            currentStep={currentStep}
            onStepChange={setCurrentStep}
          />
        </div>
        <p className="text-terminal-muted text-sm leading-relaxed">
          {stepContent.description}
        </p>
      </InfoPanel>

      {/* Without Alternate Screen */}
      <div className="bg-terminal-highlight border border-terminal-border p-6 space-y-4">
        <h3 className="text-terminal-red text-sm font-bold">
          {altUi.withoutAltTitle}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-terminal-fg font-bold text-sm">
              {altUi.withoutAltHeading}
            </div>
            <div className="bg-terminal-bg border border-terminal-border p-3 font-mono text-xs space-y-1">
              <div className="text-terminal-muted">{altUi.monoLs}</div>
              <div className="text-terminal-muted">{altUi.monoFileListing}</div>
              <div className="text-terminal-muted">{altUi.monoVimReadme}</div>
              <div className="text-terminal-yellow">{altUi.monoMarkdownTitle}</div>
              <div>{altUi.monoTilde}</div>
              <div>{altUi.monoTilde}</div>
              <div className="text-terminal-dim">{altUi.monoInsertBanner}</div>
              <div className="text-terminal-muted">{altUi.monoQuitEllipsis}</div>
              <div className="text-terminal-red">
                {altUi.withoutAltMessyFooter}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-terminal-fg font-bold text-sm">
              {altUi.withAltHeading}
            </div>
            <div className="bg-terminal-bg border border-terminal-border p-3 font-mono text-xs space-y-1">
              <div className="text-terminal-muted">{altUi.monoLs}</div>
              <div className="text-terminal-muted">{altUi.monoFileListing}</div>
              <div className="text-terminal-muted">{altUi.monoVimReadme}</div>
              <div className="text-terminal-dim">{altUi.vimOpensComment}</div>
              <div className="text-terminal-dim">{altUi.vimQuitComment}</div>
              <div className="text-terminal-muted">{altUi.monoLs}</div>
              <div className="text-terminal-muted">{altUi.monoFileListing}</div>
              <div className="text-terminal-green">{altUi.withAltFooter}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
